/**
 * Sidebar = f(user group, org role, personal prefs)   (screenshots/17-navigation-system.png)
 *
 *   1. GROUP  decides which tabs/features EXIST and their default order + starter views.
 *   2. ROLE   (Better Auth org role) gates admin items inside team accounts.
 *   3. PREFS  (per person) reorder, hide, and add custom views. Can only narrow, never unlock.
 *
 * `resolveSidebar` is pure so the Worker (for the inlined snapshot / first paint) and the
 * client compute the exact same sidebar.
 */

import { FEATURES, defaultFlags, type FeatureDef } from "../auth/features";
import { rolePermits } from "../auth/permissions";

export type UserGroup = "resident" | "office" | "advocacy";
export type OrgRole = "owner" | "admin" | "member";

export type TabId =
  | "overview" | "meetings" | "people" | "organizations"
  | "legislation" | "analytics"
  | "casework" | "campaigns";               // design section 11 (features casework / campaigns); Commitments was removed (Oct 2026); old prefs that mention it are ignored

/**
 * Bottom of the rail: Admin (/dashboard/admin, the staff dashboard) for Chicago Politics staff only (resolveSidebar
 * `staff`). Settings and Members & roles are no rail items any more: Settings is in the user menu at the top of the
 * rail (Sidebar.tsx UserMenu), Members & roles lives in Team settings (/dashboard/settings/access, feature `access`);
 * their entries stay for breadcrumbs and old prefs.
 */
export type UtilityId = "access" | "settings" | "approvals";

export interface TabDef { id: TabId | UtilityId; label: string; icon: string; path: string }

export const TABS: Record<TabId | UtilityId, TabDef> = {
  overview:      { id: "overview",      label: "Overview",      icon: "layout-grid",  path: "/dashboard" },
  meetings:      { id: "meetings",      label: "Meetings",      icon: "calendar-days", path: "/dashboard/meetings" },
  people:        { id: "people",        label: "People",        icon: "users",        path: "/dashboard/people" },
  organizations: { id: "organizations", label: "Organizations", icon: "building-2",   path: "/dashboard/organizations" },
  legislation:   { id: "legislation",   label: "Legislation",   icon: "scroll-text",  path: "/dashboard/legislation" },
  analytics:     { id: "analytics",     label: "Analytics",     icon: "chart-column", path: "/dashboard/analytics" },
  casework:      { id: "casework",      label: "Casework",      icon: "inbox",        path: "/dashboard/casework" },
  campaigns:     { id: "campaigns",     label: "Campaigns",     icon: "megaphone",    path: "/dashboard/campaigns" },
  access:        { id: "access",        label: "Members & roles", icon: "shield-check", path: "/dashboard/settings/access" },
  settings:      { id: "settings",      label: "Settings",      icon: "settings",     path: "/dashboard/settings" },
  approvals:     { id: "approvals",     label: "Admin",         icon: "shield",       path: "/dashboard/admin" },
};

/**
 * The admin dashboard (/dashboard/admin/…): Chicago Politics employees' own sidebar, one page per item, with none of
 * the regular dashboard's tabs (Shell `admin`, Sidebar `admin`). The Worker decides who is staff (dashboard-staff.js)
 * and enforces it on every page and API; `resolveSidebar(…, staff)` only adds the Admin entry to the regular rail.
 */
export type AdminTabId = "approvals" | "orgs" | "analysis" | "articles" | "alerts" | "signers" | "traffic" | "ads";
export interface AdminTabDef { id: AdminTabId; label: string; icon: string; path: string }
export const ADMIN_TABS: AdminTabDef[] = [
  { id: "approvals", label: "Approvals",     icon: "inbox",      path: "/dashboard/admin" },
  { id: "orgs",      label: "Organizations", icon: "building-2", path: "/dashboard/admin/orgs" },
  { id: "analysis",  label: "Analysis",      icon: "activity",   path: "/dashboard/admin/analysis" },
  { id: "articles",  label: "Articles",      icon: "newspaper",  path: "/dashboard/admin/articles" },
  { id: "alerts",    label: "Alerts",        icon: "mail",       path: "/dashboard/admin/alerts" },
  { id: "signers",   label: "Petition signers", icon: "map-pin", path: "/dashboard/admin/signers" },
  { id: "traffic",   label: "Traffic",       icon: "trending-up", path: "/dashboard/admin/traffic" },
  { id: "ads",       label: "Ads",           icon: "megaphone",  path: "/dashboard/admin/ads" },
];
/** Which admin item a path is on (Approvals only at /dashboard/admin itself). */
export const adminTabOf = (path: string): AdminTabId | null => {
  const p = path.replace(/\/+$/, "");
  if (p === "/dashboard/admin") return "approvals";
  return ADMIN_TABS.find((t) => t.id !== "approvals" && (p === t.path || p.startsWith(t.path + "/")))?.id ?? null;
};

/** Features a group can have. Checked on the server too (API + Durable Objects). */
export type Feature =
  | "analytics" | "organizations" | "teamFollows" | "teamViews" | "sharedNotes"
  | "csvExport" | "access" | "orgAlertDefaults"
  // Analytics dashboards (screenshots/82–86)
  | "dashboards"                                        // create, star, share inside the team
  | "publicLinks"                                       // "Anyone with the link" (owner/admin)
  // People tab (screenshots/21–24)
  | "peek" | "compare" | "flag"                         // everyone
  | "myAlderperson" | "myStances" | "claimProfile"      // residents
  | "personNotes" | "constituents"                      // alderperson offices
  | "targetLists" | "briefMe" | "alignment"             // lobbyists / advocacy
  // Legislation tab + vote forecast (screenshots/25–28)
  | "forecast" | "influence"                            // everyone: forecasts + read-only lobbying table
  | "wardVoice" | "whipCount"                           // alderperson offices
  | "yourWay" | "forecastAlerts"                        // lobbyists / advocacy (perspective switch, 10-pt alerts)
  // Organizations tab (screenshots/56–66)
  | "coalitions" | "oppositionWatch"                    // advocacy
  | "meetingLog"                                        // offices (team-only notes)
  // Casework (offices) and Campaigns (the Advocacy add-on), design section 11
  | "casework" | "campaigns";

/** A group's default features, from the shared catalog (auth/features.ts; the Worker enforces the same table). */
const groupFeatures = (group: UserGroup): Feature[] => defaultFlags(group) as Feature[];

export interface GroupDef {
  label: string;
  account: "individual" | "team";
  /** Tabs that exist for this group, in default order. */
  tabs: TabId[];
  /** Tabs that exist but start hidden (user can show them). */
  hiddenByDefault: TabId[];
  features: Feature[];
  /** Why a gated tab is missing; shown under "Not available" in Edit sidebar. */
  unavailableReason: Partial<Record<TabId | UtilityId, string>>;
}

export const GROUPS: Record<UserGroup, GroupDef> = {
  resident: {
    label: "Resident",
    account: "individual",
    tabs: ["overview", "meetings", "legislation", "people", "organizations"],
    hiddenByDefault: ["organizations"],
    features: groupFeatures("resident"),
    unavailableReason: { analytics: "For offices & advocacy groups" },
  },
  office: {
    label: "Alderperson's office",
    account: "team",
    tabs: ["overview", "meetings", "people", "legislation", "organizations", "analytics", "casework"],
    hiddenByDefault: [],
    features: groupFeatures("office"),
    unavailableReason: {},
  },
  advocacy: {
    label: "Lobbyist / advocacy",
    account: "team",
    tabs: ["overview", "legislation", "meetings", "organizations", "people", "analytics", "campaigns"],
    hiddenByDefault: [],
    features: groupFeatures("advocacy"),
    unavailableReason: {},
  },
};

/** The server's answer per feature for one viewer (snapshot:me.features: entitlements with per-org overrides + role). */
export type FeatureMap = Partial<Record<Feature, boolean>>;

// The signed-in viewer's map in the browser (main.tsx sets it from snapshot:me). The Worker never sets it: server code
// passes the principal's featureMap() to can() / the pure models explicitly.
let viewer: { group: UserGroup; role: OrgRole | null; features: FeatureMap } | null = null;
/** The page's viewer: can(group, role, …) for that same group and role answers from `features`. null clears it. */
export function setViewerFeatures(group: UserGroup | null, role: OrgRole | null, features: FeatureMap | null | undefined) {
  viewer = group && features ? { group, role: role ?? null, features } : null;
}
/** The page viewer's role in their team (null: not on a team, or no snapshot:me). */
export const viewerRole = (): OrgRole | null => viewer?.role ?? null;

/**
 * Layers 1 + 2 for the client UI and the pure models (docs/PERMISSIONS.md). With the server's map (`features`, or the
 * viewer's from setViewerFeatures when group and role are the viewer's), its answer: entitlements with this org's
 * staff overrides, and the role. Otherwise the group defaults and the role, from the same catalog the Worker enforces
 * (auth/features.ts + auth/permissions.ts); tests and pages without snapshot:me use that fallback. Admin-only items
 * inside team accounts (access, publicLinks, orgAlertDefaults) are the features with a `permission` that only owners
 * and admins hold. Tag vocabulary + team positions are admin-managed; using tags/notes/targets is open to members.
 */
export function can(group: UserGroup, role: OrgRole | null, feature: Feature, features?: FeatureMap | null): boolean {
  const map = features ?? (viewer && viewer.group === group && viewer.role === (role ?? null) ? viewer.features : null);
  if (map && typeof map[feature] === "boolean") return map[feature]!;
  if (!GROUPS[group].features.includes(feature)) return false;
  return rolePermits(role, (FEATURES[feature] as FeatureDef).permission);
}

/** A saved filter on an existing tab, e.g. "Ward 32" = meetings?ward=32. */
export interface CustomView {
  id: string;
  label: string;
  tab: TabId;
  filters: Record<string, string | string[]>;
  badge: { type: "legislation" | "person" | "topic" | "committee"; personId?: string };
  showCount: boolean;
  scope: "personal" | "team";   // team views: created by owner/admin, shown to everyone in the org
}

export interface NavPrefs {
  order: TabId[];                 // user's order (may be missing tabs added later)
  hidden: TabId[];                // user-hidden tabs
  hiddenTeamViews: string[];      // members can hide team views for themselves
  views: CustomView[];            // personal views, in order
  knownTabs: TabId[];             // tabs that existed when prefs were last saved (detects new tabs)
  starred: string[];              // starred dashboard ids, in sidebar order (Analytics)
}

export const emptyPrefs = (): NavPrefs => ({ order: [], hidden: [], hiddenTeamViews: [], views: [], knownTabs: [], starred: [] });

/** What the sidebar needs to know about a dashboard the viewer can open. */
export interface DashboardLink { id: string; title: string }
export const dashboardPath = (id: string) => `/dashboard/analytics/${id}`;

export interface ResolvedSidebar {
  tabs: TabDef[];                           // visible, in order (overview first)
  hidden: TabDef[];                         // available but hidden by the user/group default
  unavailable: { tab: TabDef; reason: string }[]; // gated by group (Edit sidebar "Not available")
  teamViews: CustomView[];
  personalViews: CustomView[];
  /** Starred dashboards under Analytics (only ones the viewer can still open, in starred order). */
  starredDashboards: { id: string; label: string; path: string }[];
  utilities: TabDef[];                      // bottom: Admin (staff only; empty for everyone else)
  canPublishTeamViews: boolean;
}

/**
 * Merge rules:
 *  - Overview is always first and always visible.
 *  - Tabs the group doesn't have are dropped from prefs (even if the user had them before).
 *  - Tabs that exist now but weren't in `knownTabs` are NEW: inserted at their group-default
 *    position relative to their neighbours, without disturbing the user's order.
 *  - Hidden = user-hidden, plus group `hiddenByDefault` for tabs the user has never seen.
 */
export function resolveSidebar(group: UserGroup, role: OrgRole | null, prefs: NavPrefs, teamViews: CustomView[] = [], dashboards: DashboardLink[] = [], staff = false): ResolvedSidebar {
  const g = GROUPS[group];
  const known = (t: string): t is TabId => t in TABS && t !== "access" && t !== "settings" && t !== "approvals";
  const allowed = g.tabs.filter((t) => t !== "analytics" || can(group, role, "analytics"))
                        .filter((t) => t !== "organizations" || can(group, role, "organizations"))
                        .filter((t) => (t !== "casework" && t !== "campaigns") || can(group, role, t));

  // 1. user order, filtered to what's allowed
  const order = prefs.order.filter((t) => known(t) && allowed.includes(t));
  // 2. insert tabs the user hasn't placed yet at their default position
  for (const t of allowed) {
    if (order.includes(t)) continue;
    const defIdx = allowed.indexOf(t);
    const prevNeighbour = allowed.slice(0, defIdx).reverse().find((x) => order.includes(x));
    const at = prevNeighbour ? order.indexOf(prevNeighbour) + 1 : 0;
    order.splice(at, 0, t);
  }
  // 3. overview pinned first
  const finalOrder: TabId[] = ["overview", ...order.filter((t) => t !== "overview")];

  const neverSeen = (t: TabId) => !prefs.knownTabs.includes(t);
  const isHidden = (t: TabId) =>
    t !== "overview" && (prefs.hidden.includes(t) || (g.hiddenByDefault.includes(t) && neverSeen(t)));

  const unavailable = (Object.keys(TABS) as (TabId | UtilityId)[])
    .filter((id) => g.unavailableReason[id])
    .map((id) => ({ tab: TABS[id], reason: g.unavailableReason[id]! }));

  return {
    tabs: finalOrder.filter((t) => !isHidden(t)).map((t) => TABS[t]),
    hidden: finalOrder.filter(isHidden).map((t) => TABS[t]),
    unavailable,
    teamViews: can(group, role, "teamViews") || g.account === "team"
      ? teamViews.filter((v) => !prefs.hiddenTeamViews.includes(v.id) && allowed.includes(v.tab))
      : [],
    personalViews: prefs.views.filter((v) => allowed.includes(v.tab)),
    starredDashboards: allowed.includes("analytics") && !isHidden("analytics")
      ? (prefs.starred ?? []).map((id) => dashboards.find((d) => d.id === id)).filter((d): d is DashboardLink => !!d)
          .map((d) => ({ id: d.id, label: d.title, path: dashboardPath(d.id) }))
      : [],
    // Staff (the platform team with a staff-domain email; the Worker decides and enforces it): the admin dashboard.
    utilities: staff ? [TABS.approvals] : [],
    canPublishTeamViews: g.account === "team" && (role === "owner" || role === "admin"),
  };
}

/** Pure reorder used by drag-and-drop and keyboard moves. Overview can't move. */
export function moveTab(order: TabId[], tab: TabId, toIndex: number): TabId[] {
  if (tab === "overview") return order;
  const without = order.filter((t) => t !== tab);
  const idx = Math.max(1, Math.min(toIndex, without.length)); // index 0 is reserved for overview
  return [...without.slice(0, idx), tab, ...without.slice(idx)];
}
